import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | error | timeout
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_50%_40%,rgba(0,240,255,0.14),transparent_45%)]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative text-center max-w-md" data-testid="payment-success">
        {status === "checking" && (
          <><Loader2 size={48} className="text-[#00F0FF] animate-spin mx-auto" /><p className="mt-6 text-white/70">Confirming your subscription…</p></>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={64} className="text-[#00E676] mx-auto" />
            <h1 className="font-heading font-extrabold text-3xl tracking-tighter text-white mt-6">You're Premium!</h1>
            <p className="mt-3 text-white/60">Ads are now off. Enjoy uninterrupted streaming.</p>
            <Link to="/" className="inline-block mt-8 rounded-full bg-[#00F0FF] text-black font-semibold px-8 py-3 hover:scale-105 transition-transform">Start Watching</Link>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle size={56} className="text-[#FF2A2A] mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-white mt-6">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="mt-3 text-white/60">{status === "timeout" ? "It's taking a moment. Your access will update shortly." : "We couldn't confirm your payment."}</p>
            <Link to="/premium" className="inline-block mt-8 rounded-full bg-white/10 border border-white/20 text-white font-medium px-8 py-3 hover:bg-white/20 transition-colors">Back to Premium</Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
